import React, { useMemo, useState } from "react";
import { View, Text, TouchableOpacity, StyleSheet, Pressable } from "react-native";

import CampTypes, { getCampTypeIconName } from "../../../../data/CampTypes";
import { MaterialIcons } from "@expo/vector-icons";

type FilterModalCategoriesProps = {
    selectedCategories: string[];
    onChange: (newCategories: string[]) => void;
};

const FilterModalCategories = ({ selectedCategories, onChange }: FilterModalCategoriesProps) => {
    const [isCategoriesExpanded, setIsCategoriesExpanded] = useState(false);

    const categories = useMemo(() => (
        CampTypes.filter(type => type.text !== 'All').map(type => type.text)
    ), []);

    const CATEGORIES_PER_ROW = 6;
    const INITIAL_ROWS = 2;
    const initialCategoriesCount = CATEGORIES_PER_ROW * INITIAL_ROWS;
    const displayedCategories = isCategoriesExpanded ? categories : categories.slice(0, initialCategoriesCount);

    const handleClear = () => onChange([]);

    const toggleCategory = (category: string) => {
        const newCategories = selectedCategories.includes(category)
            ? selectedCategories.filter(item => item !== category)
            : [...selectedCategories, category];
        onChange(newCategories);
    };

    return (
        <View style={{ marginBottom: 24 }}>
            <Text style={styles.sectionTitle}>Categories</Text>

            <View style={[styles.categoryContainer, { backgroundColor: '#FFFFFF', borderRadius: 16, padding: 8, borderWidth: 1, borderColor: '#E5E7EB' }]}>
                <TouchableOpacity
                    onPress={handleClear}
                    style={[styles.textBanner, selectedCategories.length === 0 && styles.selectedBanner]}
                >
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <MaterialIcons name="all-inclusive" style={{ fontSize: 16, color: selectedCategories.length === 0 ? '#FFFFFF' : '#374151' }} />
                        <Text style={{ color: selectedCategories.length === 0 ? '#FFFFFF' : '#111827', fontWeight: '600' }}>All</Text>
                    </View>
                </TouchableOpacity>

                {displayedCategories.map((category, index) => {
                    const iconName = getCampTypeIconName(category);

                    return (
                        <TouchableOpacity
                            key={index}
                            onPress={() => toggleCategory(category)}
                            style={[styles.textBanner, selectedCategories.includes(category) && styles.selectedBanner]}
                        >
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                <MaterialIcons
                                    name={iconName as any}
                                    style={{ fontSize: 16, color: selectedCategories.includes(category) ? '#FFFFFF' : '#374151' }}
                                />
                                <Text style={{ color: selectedCategories.includes(category) ? '#FFFFFF' : '#111827', fontWeight: '600' }}>{category}</Text>
                            </View>
                        </TouchableOpacity>
                    );
                })}
            </View>

            {categories.length > initialCategoriesCount && (
                <Pressable onPress={() => setIsCategoriesExpanded(!isCategoriesExpanded)} style={styles.expandButton}>
                    {isCategoriesExpanded ? (
                        <MaterialIcons name="expand-less" size={20} color="#111827" />
                    ) : (
                        <MaterialIcons name="expand-more" size={20} color="#111827" />
                    )}
                    <Text style={styles.expandButtonText}>
                        {isCategoriesExpanded ? 'Show less' : `Show ${categories.length - initialCategoriesCount} more`}
                    </Text>
                </Pressable>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    sectionTitle: {
        fontSize: 16,
        fontWeight: '700',
        marginBottom: 14,
        color: '#111827'
    },
    textBanner: {
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: 999,
        backgroundColor: 'white',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        margin: 6,
        flexBasis: '31.5%',
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOpacity: 0.04,
        shadowRadius: 4,
        shadowOffset: { width: 0, height: 2 },
        elevation: 1,
    },
    categoryContainer: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        width: '100%',
    },
    selectedBanner: {
        backgroundColor: '#3C8248',
    },
    expandButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 10,
        marginTop: 10,
    },
    expandButtonText: {
        marginLeft: 5,
        fontSize: 14,
        fontWeight: '500',
        color: '#374151',
    },
});

export default FilterModalCategories;


